import { useMutation, useQueryClient } from "@tanstack/react-query"
import api from "@/lib/axios"
import FormHeader from "@/components/FormHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useState } from "react"
import { FaPlus } from "react-icons/fa6"
import { Loader2 } from "lucide-react"
import { KepemilikanAsset } from "@/types"
import { MdModeEdit } from "react-icons/md"
import { RiDeleteBack2Line } from "react-icons/ri"
import toast from "react-hot-toast"
import { AxiosError } from "axios"
import { Switch } from "@/components/ui/switch"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

const postData = async (newData: KepemilikanAsset) => {
  const response = await api.post("/master/kepemilikan-aset/store", newData)
  return response.data
}

const patchData = async (newData: KepemilikanAsset) => {
  const response = await api.patch("/master/kepemilikan-aset/update", newData)
  return response.data
}

const deleteData = async (deletedData: { id_ref_aset_kepemilikan: number }) => {
  const response = await api({
    url: "/master/kepemilikan-aset/delete",
    method: "DELETE",
    data: deletedData,
  })
  return response.data
}

export default function Form({
  method,
  selectedRow,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE"
  selectedRow?: KepemilikanAsset | null
  closeModal: () => void
}) {
  const [form, setForm] = useState<KepemilikanAsset>({
    id_ref_aset_kepemilikan: selectedRow?.id_ref_aset_kepemilikan || 0,
    nama_kepemilikan: selectedRow?.nama_kepemilikan || "",
    skor_kepemilikan: selectedRow?.skor_kepemilikan || 0,
    is_aktif: selectedRow?.is_aktif ?? true,
    kode_kepemilikan: null,
  })

  const isPost = method === "POST"
  const isPatch = method === "PATCH"
  const isDelete = method === "DELETE"

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listKepemilikanAset"] })
      closeModal()
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mutate(form)
  }

  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Delete Data Kepemilikan Asset"
          description={`Apakah anda yakin akan menghapus data "${selectedRow?.nama_kepemilikan}"?`}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-3"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Hapus"
            )}
          </Button>
        </div>
      </form>
    )

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={
          isPost
            ? "Insert Data Kepemilikan Asset"
            : "Update Data Kepemilikan Asset"
        }
      />
      <div>
        <Label>Kode Kepemilikan Asset</Label>
        <Select
          value={form.kode_kepemilikan || ""}
          onValueChange={(value) => {
            setForm((prev) => ({
              ...prev,
              kode_kepemilikan: value as
                | "sendiri"
                | "orangtua"
                | "sewa"
                | "menumpang"
                | "lainnya",
            }))
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder={"Pilih Kode Kepemilikan Asset"}>
              {form.kode_kepemilikan &&
                ["sendiri", "orangtua", "sewa", "menumpang", "lainnya"]?.find(
                  (value) => value === form.kode_kepemilikan
                )}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {["sendiri", "orangtua", "sewa", "menumpang", "lainnya"]?.map(
              (value, index) => (
                <SelectItem key={index} value={`${value}`}>
                  {value}
                </SelectItem>
              )
            )}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Nama Kepemilikan Asset</Label>
        <Input
          placeholder="Ketik nama kepemilikan asset..."
          value={form.nama_kepemilikan}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              nama_kepemilikan: event.target.value,
            }))
          }
        />
      </div>
      <div>
        <Label>Skor</Label>
        <Input
          type="number"
          placeholder="Ketik skor..."
          value={form.skor_kepemilikan}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              skor_kepemilikan: Number(event.target.value),
            }))
          }
        />
      </div>

      <div className="flex gap-3">
        <Switch
          id="status"
          checked={form.is_aktif}
          onCheckedChange={() =>
            setForm((prev) => ({
              ...prev,
              is_aktif: !prev.is_aktif,
            }))
          }
        />
        <Label htmlFor="status">Status Nonaktif/Aktif</Label>
      </div>

      <Button className="mt-3 flex items-center gap-3" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        ) : (
          "Simpan"
        )}
      </Button>
    </form>
  )
}
